<template>
  <div class="audit-page">
    <section class="audit-toolbar">
      <div class="audit-toolbar__copy">
        <div class="audit-toolbar__title">内容审核管理</div>
        <div class="audit-toolbar__desc">
          微信图片审核的结果都在这里。审核中超过 10 分钟没有回调的会标成「卡住」——
          可以重新提交审核，也可以直接手动通过（内容立刻恢复可见）或手动驳回（下架并通知用户）。
        </div>
      </div>
      <div class="audit-toolbar__actions">
        <span v-if="stuckCount > 0" class="stuck-tip">有 {{ stuckCount }} 条卡住的审核</span>
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
        <button class="primary-button" type="button" :disabled="releasing" @click="releaseStuck">
          {{ releasing ? "处理中…" : "一键放行卡住的审核" }}
        </button>
      </div>
    </section>

    <section class="audit-card">
      <div class="audit-filters">
        <select v-model="filters.status" class="filter-select" @change="reload">
          <option value="">全部状态</option>
          <option value="REVIEWING">审核中</option>
          <option value="PASS">已通过</option>
          <option value="REJECTED">已驳回</option>
          <option value="SUPERSEDED">已作废</option>
        </select>
        <select v-model="filters.targetType" class="filter-select" @change="reload">
          <option value="">全部类型</option>
          <option value="SQUARE_POST">广场帖子</option>
          <option value="SQUARE_COMMENT">广场评论</option>
          <option value="ALBUM_ASSET">相册素材</option>
          <option value="USER_AVATAR">用户头像</option>
          <option value="USER_BACKGROUND">主页背景</option>
        </select>
        <input
          v-model="filters.keyword"
          class="filter-input"
          type="text"
          placeholder="搜索：内容ID / 任务ID / 图片地址"
          @keyup.enter="reload"
        />
        <button class="primary-button" type="button" @click="reload">搜索</button>
      </div>

      <div v-if="loading" class="list-state">正在加载审核记录…</div>
      <div v-else-if="!rows.length" class="list-state">没有符合条件的审核记录。</div>
      <table v-else class="audit-table">
        <thead>
          <tr>
            <th>内容</th>
            <th>素材</th>
            <th>状态</th>
            <th>微信返回</th>
            <th>时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="String(row.id)">
            <td>
              <div class="target-line">
                <span class="target-type">{{ row.targetTypeLabel }}</span>
                <span class="target-id">#{{ row.targetId }}</span>
              </div>
              <div class="target-summary">{{ row.targetSummary || "—" }}</div>
              <div class="target-owner" v-if="row.ownerNickname">{{ row.ownerNickname }}（UID {{ row.ownerUserId }}）</div>
            </td>
            <td>
              <img v-if="isImage(row.mediaUrl)" class="media-thumb" :src="resolveFileUrl(row.mediaUrl)" alt="" />
              <span v-else class="media-text">{{ row.mediaUrl }}</span>
            </td>
            <td>
              <span class="status-pill" :class="statusClass(row.status)">
                {{ row.statusLabel }}
              </span>
              <span v-if="row.stuck" class="stuck-badge">卡住</span>
            </td>
            <td>
              <div class="wx-line">errcode: {{ row.errcode === null || row.errcode === undefined ? "—" : row.errcode }}</div>
              <div class="wx-line" v-if="row.suggest">suggest: {{ row.suggest }}</div>
            </td>
            <td class="time-cell">{{ formatTime(row.createdAt) }}</td>
            <td class="row-actions">
              <button class="link-button" type="button" @click="approve(row)">手动通过</button>
              <button class="link-button link-button--danger" type="button" @click="reject(row)">驳回</button>
              <button class="link-button" type="button" @click="resubmit(row)">重新提交</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="total > pageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
        <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
        <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
      </div>

      <div v-if="message" class="page-message" :class="{ 'page-message--error': messageError }">{{ message }}</div>
    </section>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import {
  approveContentAudit,
  fetchContentAudits,
  rejectContentAudit,
  releaseStuckContentAudits,
  resubmitContentAudit
} from "../api/contentAudit"

export default {
  name: "ContentAuditView",
  data() {
    return {
      loading: false,
      releasing: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { status: "", targetType: "", keyword: "" },
      message: "",
      messageError: false
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    },
    stuckCount() {
      return this.rows.filter(item => item.stuck).length
    }
  },
  created() {
    this.reload()
  },
  methods: {
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    isImage(value) {
      return !!value && /\.(jpg|jpeg|png|gif|webp)$/i.test(value)
    },
    statusClass(status) {
      if (status === "PASS") return "status-pill--pass"
      if (status === "REJECTED") return "status-pill--rejected"
      if (status === "SUPERSEDED") return "status-pill--superseded"
      return "status-pill--reviewing"
    },
    formatTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    setMessage(text, isError) {
      this.message = text
      this.messageError = !!isError
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchContentAudits({
          status: this.filters.status || undefined,
          targetType: this.filters.targetType || undefined,
          keyword: this.filters.keyword || undefined,
          page: this.page,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.rows = data.records || []
        this.total = Number(data.total || 0)
      } catch (error) {
        this.setMessage((error && error.message) || "审核记录加载失败", true)
      } finally {
        this.loading = false
      }
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.reload()
    },
    async approve(row) {
      try {
        await approveContentAudit(row.id)
        this.setMessage("已手动通过，内容已恢复可见", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async reject(row) {
      const reason = window.prompt("驳回理由（会通知用户）", "内容不符合社区规范")
      if (reason === null) return
      try {
        await rejectContentAudit(row.id, reason)
        this.setMessage("已驳回并下架", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async resubmit(row) {
      try {
        await resubmitContentAudit(row.id)
        this.setMessage("已重新提交微信审核，等结果回来会自动更新", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "重新提交失败", true)
      }
    },
    async releaseStuck() {
      if (!window.confirm("把审核中超过 10 分钟、一直没回调的素材全部放行？放行后相关内容会立刻可见。")) {
        return
      }
      this.releasing = true
      try {
        const res = await releaseStuckContentAudits(10)
        this.setMessage(res.message || "已放行卡住的审核", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      } finally {
        this.releasing = false
      }
    }
  }
}
</script>

<style scoped>
.audit-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.audit-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  border-radius: 16px;
  background: linear-gradient(120deg, #eef4ff 0%, #ffffff 60%);
  border: 1px solid #dbe6fb;
}

.audit-toolbar__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.audit-toolbar__desc {
  margin-top: 8px;
  max-width: 760px;
  font-size: 13px;
  line-height: 20px;
  color: #6b7280;
}

.audit-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stuck-tip {
  font-size: 13px;
  color: #b45309;
}

.audit-card {
  padding: 20px 22px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eceff3;
}

.audit-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  color: #1f2937;
  box-sizing: border-box;
}

.filter-input {
  flex: 1;
  max-width: 340px;
}

.primary-button,
.ghost-button {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
}

.primary-button {
  background: #111827;
  color: #ffffff;
}

.primary-button:disabled,
.ghost-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.ghost-button {
  background: #ffffff;
  border-color: #dfe3e8;
  color: #374151;
}

.list-state {
  padding: 26px 0;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.audit-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.audit-table th,
.audit-table td {
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #f1f3f6;
  vertical-align: top;
}

.audit-table th {
  font-size: 12px;
  color: #8b93a1;
  font-weight: 600;
}

.target-type {
  font-weight: 700;
  color: #111827;
}

.target-id {
  margin-left: 6px;
  color: #8b93a1;
}

.target-summary {
  margin-top: 4px;
  max-width: 260px;
  color: #4b5563;
  line-height: 19px;
}

.target-owner {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.media-thumb {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
}

.media-text {
  display: inline-block;
  max-width: 220px;
  word-break: break-all;
  font-size: 12px;
  color: #6b7280;
}

.status-pill {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.status-pill--reviewing {
  background: #fff5e5;
  color: #b45309;
}

.status-pill--pass {
  background: #e7f6ec;
  color: #1d8a4b;
}

.status-pill--rejected {
  background: #fdecea;
  color: #b23f2c;
}

.status-pill--superseded {
  background: #f2f4f7;
  color: #8b93a1;
}

.stuck-badge {
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fdecea;
  color: #b23f2c;
  font-size: 11px;
}

.wx-line {
  font-size: 12px;
  color: #6b7280;
}

.time-cell {
  white-space: nowrap;
  color: #6b7280;
}

.row-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

.link-button {
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
}

.link-button--danger {
  color: #dc2626;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
}

.pager-text {
  font-size: 13px;
  color: #6b7280;
}

.page-message {
  margin-top: 12px;
  font-size: 13px;
  color: #1d8a4b;
}

.page-message--error {
  color: #dc2626;
}
</style>
